// NDR Exhausts — chat/search widget
// Fully client-side: no API calls, no backend, no cost. Answers FAQs with
// canned responses and searches a live catalog snapshot built from the
// actual Shopify product list below.
// NDR_URLS and NDR_CATALOG are set by snippets/chat-catalog-data.liquid,
// rendered inline from the layout before this script loads.

const GREETINGS = [
  "Hey there! Ask me about a specific car — like \"Huracán exhaust\" or \"M3 downpipes\" — or about shipping, returns, or warranty. What are you driving?",
  "Hi! I can help you find the right system for your car, or answer questions about shipping, returns, and warranty. What's on your mind?",
  "Hey! What are you working with — got a make and model in mind, or a question about an order?",
];

const THANKS_REPLIES = [
  "Anytime! Let me know if there's anything else I can help you track down.",
  "You got it. Happy to help with anything else — just ask.",
  "No problem at all! I'm here if you think of anything else.",
];

const PRODUCT_FOUND_INTROS = [
  "Good news — we've got something for that:",
  "Found it:",
  "Here's what came up:",
  "We've got you covered on this one:",
];

const PRODUCT_MULTI_INTROS = [
  "Here's what I found that might fit:",
  "A few options came up — take a look:",
  "Got a couple matches for you:",
];

const NOT_FOUND_REPLIES = [
  "Hmm, I'm not seeing that exact one in our catalogue yet. Try browsing the Shop page, or tell us what you're driving on the Contact page and we'll follow up personally.",
  "I couldn't track that down in our current lineup. It might be worth a look on the Shop page, or send us your vehicle details on Contact and we'll get back to you directly.",
  "That one's not in the catalogue right now, sorry! Browse the full Shop page, or reach out on Contact and we'll see what we can do for your car.",
];

const STOPWORDS = new Set([
  "a", "an", "the", "is", "are", "do", "does", "did", "you", "your", "i", "im", "i'm",
  "have", "has", "had", "for", "my", "me", "to", "of", "in", "on", "at", "and", "or",
  "with", "about", "any", "anything", "please", "hi", "hello", "hey", "want", "need",
  "looking", "car", "get", "got", "can", "could", "would", "will", "there", "it", "its",
  "this", "that", "what", "whats", "how", "when", "where", "if", "so", "just", "one",
]);

const FAQ_RULES = [
  {
    keywords: ["ship", "shipping", "deliver", "delivery", "lead time", "how long"],
    text:
      "Free ground shipping on all systems within the continental US. Catalogue systems ship in 2–3 weeks; custom one-off builds run 6–10 weeks. Alaska, Hawaii, and international orders — reach out on the Contact page for a shipping quote.",
  },
  {
    keywords: ["return", "refund", "send back", "exchange"],
    text:
      "Uninstalled, unused catalogue items can be returned within 30 days of delivery (original packaging, no install marks) — a 15% restocking fee applies, and you cover return shipping unless it's our error. Custom builds and limited-availability items are final sale.",
    link: { text: "View full return policy", href: NDR_URLS.returns },
  },
  {
    keywords: ["cancel", "cancellation"],
    text:
      "You can cancel free of charge within 24 hours of ordering, before fabrication starts. Once a system enters production it's being built for your specific vehicle and can't be canceled.",
  },
  {
    keywords: ["warranty", "guarantee"],
    text:
      "Every system carries a lifetime craftsmanship warranty on welds. Valve control electronics carry a 3-year manufacturer warranty. That's separate from the 30-day return window.",
  },
  {
    keywords: ["defect", "damaged", "wrong item", "broken", "faulty"],
    text:
      "If your order arrives defective, damaged, or wrong, contact us within 7 days with photos — we'll send a prepaid return label and cover a full replacement or refund, no restocking fee.",
    link: { text: "Contact us", href: NDR_URLS.contact },
  },
  {
    keywords: ["legal", "smog", "emission", "carb", "street legal"],
    text:
      "Most of our valved exhaust systems are 50-state emissions legal. Downpipes are catless/high-flow and intended for off-road or track use only — check your local emissions laws before installing.",
  },
  {
    keywords: ["custom", "not listed", "dont see", "don't see", "one off", "one-off"],
    text:
      "If your car isn't in our catalogue, we build custom one-off systems. Use the \"Don't See Your Car? Inquire Here\" button at the bottom of the Shop page, or send us your vehicle details on Contact.",
    link: { text: "Go to Contact", href: NDR_URLS.contact },
  },
  {
    keywords: ["buy", "purchase", "order", "checkout"],
    text:
      'Find your system on the Shop page and click into the product to check out. If you don\'t see your exact vehicle, use the Inquire button at the bottom of the Shop page.',
    link: { text: "Go to Shop", href: NDR_URLS.shop },
  },
  {
    keywords: ["contact", "phone number", "call you", "email you", "reach you", "talk to"],
    text:
      "You can reach us at info@ndrexhausts.com or (949) 482-9558, Mon–Fri 8am–5pm PT (email answered 7 days a week).",
    link: { text: "Go to Contact", href: NDR_URLS.contact },
  },
  {
    keywords: ["hour", "open", "support hours"],
    text: "Support is available Mon–Fri, 8:00am–5:00pm PT. Email is answered 7 days a week.",
  },
];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function normalize(str) {
  return String(str)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function tokenize(str) {
  const words = normalize(str)
    .replace(/[-']/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 1 && !STOPWORDS.has(t));
  // Also add adjacent-word bigrams ("gt3" + "rs" -> "gt3rs") so compound
  // codes typed as one word (GT3RS, M340i) still match catalog entries
  // that are written with a space (GT3 RS).
  const bigrams = [];
  for (let i = 0; i < words.length - 1; i++) {
    bigrams.push(words[i] + words[i + 1]);
  }
  return words.concat(bigrams);
}

function matchFAQ(message) {
  const normalized = normalize(message);
  if (["hi", "hello", "hey", "sup", "yo"].some((kw) => normalized === kw || normalized.startsWith(kw + " "))) {
    return { text: pick(GREETINGS) };
  }
  if (["thank", "thanks"].some((kw) => normalized.includes(kw))) {
    return { text: pick(THANKS_REPLIES) };
  }
  for (const rule of FAQ_RULES) {
    if (rule.keywords.some((kw) => normalized.includes(kw))) return rule;
  }
  return null;
}

function searchCatalog(message) {
  const catalog = window.NDR_CATALOG || [];
  const queryTokens = tokenize(message);
  if (queryTokens.length === 0) return [];

  const scored = catalog
    .map((item) => {
      const itemTokens = new Set(tokenize(`${item.brand} ${item.name} ${item.type}`));
      let score = 0;
      queryTokens.forEach((t) => {
        if (itemTokens.has(t)) score += 1;
      });
      return { item, score };
    })
    .filter((s) => s.score > 0);

  if (scored.length === 0) return [];

  // Only keep the best-matching tier — otherwise a broad search (e.g. one
  // brand name) gets padded out with barely-related items from other brands
  // that happen to share a generic word like "exhaust".
  const maxScore = Math.max(...scored.map((s) => s.score));
  return scored
    .filter((s) => s.score === maxScore)
    .slice(0, 5)
    .map((s) => s.item);
}

function buildProductAnswer(matches) {
  if (matches.length === 1) {
    const m = matches[0];
    return {
      text: `${pick(PRODUCT_FOUND_INTROS)} the ${m.name.replace(/^NDR\s+/, "")} — ${m.price}.`,
      links: [{ text: `View the ${m.name.replace(/^NDR\s+/, "")} →`, href: m.url }],
    };
  }
  return {
    text: pick(PRODUCT_MULTI_INTROS),
    links: matches.map((m) => ({
      text: `${m.name.replace(/^NDR\s+/, "")} — ${m.price}`,
      href: m.url,
    })),
  };
}

function getReply(message) {
  const faq = matchFAQ(message);
  const matches = searchCatalog(message);

  // If the message clearly names a product/brand, prefer showing matches —
  // but a few FAQ intents (return/ship/warranty/etc.) should always win
  // even if a brand name happens to appear in the same sentence.
  const strongFaqKeywords = [
    "return", "refund", "ship", "warranty", "cancel", "defect", "damaged",
    "legal", "smog", "emission", "contact", "phone", "email", "hour",
  ];
  const normalized = normalize(message);
  const faqIsStrong = faq && strongFaqKeywords.some((kw) => normalized.includes(kw));

  if (faqIsStrong || (faq && matches.length === 0)) {
    return faq;
  }
  if (matches.length > 0) {
    return buildProductAnswer(matches);
  }
  if (faq) {
    return faq;
  }
  return {
    text: pick(NOT_FOUND_REPLIES),
    link: { text: "Browse Shop", href: NDR_URLS.shop },
  };
}

function el(tag, className, text) {
  const e = document.createElement(tag);
  if (className) e.className = className;
  if (text) e.textContent = text;
  return e;
}

function renderMessageContent(container, reply) {
  const text = typeof reply === "string" ? reply : reply.text;
  const lines = String(text).split("\n").filter((l) => l.trim() !== "");
  (lines.length ? lines : [text]).forEach((line) => {
    const p = document.createElement("p");
    p.textContent = line;
    container.appendChild(p);
  });
  if (typeof reply !== "string") {
    const links = reply.links || (reply.link ? [reply.link] : []);
    links.forEach((link) => {
      const a = document.createElement("a");
      a.href = link.href;
      a.className = "chat-msg-link";
      a.textContent = link.text + (link.text.endsWith("→") ? "" : " →");
      container.appendChild(a);
    });
  }
}

function addMessage(role, reply) {
  const messagesEl = document.querySelector(".chat-messages");
  const msg = el("div", `chat-msg ${role}`);
  renderMessageContent(msg, reply);
  messagesEl.appendChild(msg);
  messagesEl.scrollTop = messagesEl.scrollHeight;
  return msg;
}

function showTyping() {
  const messagesEl = document.querySelector(".chat-messages");
  const typing = el("div", "chat-typing");
  typing.id = "chat-typing-indicator";
  typing.appendChild(el("span"));
  typing.appendChild(el("span"));
  typing.appendChild(el("span"));
  messagesEl.appendChild(typing);
  messagesEl.scrollTop = messagesEl.scrollHeight;
}

function hideTyping() {
  const typing = document.getElementById("chat-typing-indicator");
  if (typing) typing.remove();
}

function sendMessage(text) {
  addMessage("user", text);

  const input = document.querySelector(".chat-input-row input");
  const button = document.querySelector(".chat-input-row button");
  input.value = "";
  input.disabled = true;
  button.disabled = true;
  showTyping();

  // Small artificial delay so replies don't feel instant/robotic. Slightly
  // longer + more variable for longer messages, to mimic someone reading
  // and typing a reply rather than a lookup firing instantly.
  const delay = 500 + Math.random() * 500 + Math.min(text.length * 8, 600);
  setTimeout(() => {
    hideTyping();
    const reply = getReply(text);
    addMessage("assistant", reply);
    input.disabled = false;
    button.disabled = false;
    input.focus();
  }, delay);
}

function buildWidget() {
  const toggle = el("button", "chat-toggle");
  toggle.setAttribute("aria-label", "Chat with NDR Exhausts");
  toggle.innerHTML =
    '<svg class="chat-chat-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>' +
    '<svg class="chat-close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>';

  const panel = el("div", "chat-panel");
  panel.innerHTML =
    '<div class="chat-header">' +
    '  <div class="chat-header-info">' +
    '    <img src="//ndrexhausts.com/cdn/shop/t/21/assets/logo.png?v=94250457420504609471786300066" alt="">' +
    "    <div>" +
    "      <h4>NDR Assistant</h4>" +
    "      <span>Online</span>" +
    "    </div>" +
    "  </div>" +
    '  <button class="chat-panel-close" aria-label="Close chat">' +
    '    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
    "  </button>" +
    "</div>" +
    '<div class="chat-messages"></div>' +
    '<div class="chat-input-row">' +
    '  <input type="text" placeholder="Ask about fitment, shipping, returns..." maxlength="200">' +
    '  <button aria-label="Send">' +
    '    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 6l6 6-6 6"/></svg>' +
    "  </button>" +
    "</div>";

  document.body.appendChild(toggle);
  document.body.appendChild(panel);

  let greeted = false;
  function open() {
    panel.classList.add("open");
    toggle.classList.add("open");
    if (!greeted) {
      addMessage("assistant", pick(GREETINGS));
      greeted = true;
    }
    document.querySelector(".chat-input-row input").focus();
  }
  function close() {
    panel.classList.remove("open");
    toggle.classList.remove("open");
  }

  toggle.addEventListener("click", () => {
    if (panel.classList.contains("open")) close();
    else open();
  });
  panel.querySelector(".chat-panel-close").addEventListener("click", close);

  const input = panel.querySelector(".chat-input-row input");
  const button = panel.querySelector(".chat-input-row button");
  const submit = () => {
    const text = input.value.trim();
    if (!text) return;
    sendMessage(text);
  };
  button.addEventListener("click", submit);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") submit();
  });
}

document.addEventListener("DOMContentLoaded", buildWidget);
